import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  IconButton,
  LinearProgress,
  Chip,
  Tooltip,
  Slide,
  Badge,
  Popover,
  alpha,
  useTheme,
  Snackbar,
  Button,
  SxProps,
  Theme,
} from '@mui/material';
import {
  Close as CloseIcon,
  CloudUpload as UploadIcon,
  AccountTree as BomIcon,
  TravelExplore as ResearchIcon,
  AutoAwesome as EnrichIcon,
  CloudDownload as DatasheetBackfillIcon,
  CheckCircle as DoneIcon,
  Error as ErrorIcon,
  ClearAll as ClearAllIcon,
  PlayCircleOutline as ActiveIcon,
  NotificationsNone as ActionCenterIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useProcessMonitor } from '../contexts/ProcessMonitorContext';
import {
  UserAction,
  UserActionType,
  UserActionStatus,
} from '../services/userActionService';

const ACTION_ICONS: Record<string, React.ReactNode> = {
  [UserActionType.SPEC_UPLOAD]: <UploadIcon sx={{ fontSize: 18 }} />,
  [UserActionType.BOM_UPLOAD]: <BomIcon sx={{ fontSize: 18 }} />,
  [UserActionType.BOM_ENRICHMENT]: <EnrichIcon sx={{ fontSize: 18 }} />,
  [UserActionType.MARKET_RESEARCH]: <ResearchIcon sx={{ fontSize: 18 }} />,
  [UserActionType.PART_LIFECYCLE]: <EnrichIcon sx={{ fontSize: 18 }} />,
  [UserActionType.BULK_IMPORT]: <UploadIcon sx={{ fontSize: 18 }} />,
  [UserActionType.DATASHEET_REPROCESS]: <UploadIcon sx={{ fontSize: 18 }} />,
  [UserActionType.DATASHEET_BACKFILL]: <DatasheetBackfillIcon sx={{ fontSize: 18 }} />,
};

const STATUS_COLORS: Record<string, string> = {
  [UserActionStatus.PENDING]: '#64748b',
  [UserActionStatus.IN_PROGRESS]: '#d97706',
  [UserActionStatus.COMPLETED]: '#059669',
  [UserActionStatus.FAILED]: '#dc2626',
  [UserActionStatus.CANCELLED]: '#94a3b8',
};

const STATUS_LABELS: Record<string, string> = {
  [UserActionStatus.PENDING]: 'Queued',
  [UserActionStatus.IN_PROGRESS]: 'Processing',
  [UserActionStatus.COMPLETED]: 'Done',
  [UserActionStatus.FAILED]: 'Failed',
  [UserActionStatus.CANCELLED]: 'Cancelled',
};

function timeAgo(dateStr: string): string {
  const seconds = Math.floor((Date.now() - new Date(dateStr).getTime()) / 1000);
  if (seconds < 60) return 'just now';
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.floor(hours / 24)}d ago`;
}

/** Single action row in the expanded panel */
const ActionRow: React.FC<{
  action: UserAction;
  onDismiss: (id: string) => void;
  onNavigate: (path: string) => void;
}> = ({ action, onDismiss, onNavigate }) => {
  const isActive =
    action.status === UserActionStatus.PENDING ||
    action.status === UserActionStatus.IN_PROGRESS;
  const isDone = action.status === UserActionStatus.COMPLETED;
  const isFailed = action.status === UserActionStatus.FAILED;
  const statusColor = STATUS_COLORS[action.status] || '#64748b';

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 1.5,
        py: 1.5,
        px: 2,
        borderBottom: '1px solid',
        borderColor: 'divider',
        cursor: isDone && action.result?.navigateTo ? 'pointer' : 'default',
        transition: 'background-color 0.15s',
        '&:hover': {
          bgcolor: isDone && action.result?.navigateTo ? alpha('#0ea5e9', 0.04) : undefined,
        },
        '&:last-child': { borderBottom: 'none' },
      }}
      onClick={() => {
        if (isDone && action.result?.navigateTo) {
          onNavigate(action.result.navigateTo);
        }
      }}
    >
      {/* Icon */}
      <Box
        sx={{
          width: 32,
          height: 32,
          borderRadius: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: alpha(statusColor, 0.1),
          color: statusColor,
          flexShrink: 0,
          mt: 0.25,
        }}
      >
        {isActive ? (
          <ActiveIcon sx={{ fontSize: 18, animation: 'spin 2s linear infinite', '@keyframes spin': { '0%': { opacity: 1 }, '50%': { opacity: 0.4 }, '100%': { opacity: 1 } } }} />
        ) : isDone ? (
          <DoneIcon sx={{ fontSize: 18 }} />
        ) : isFailed ? (
          <ErrorIcon sx={{ fontSize: 18 }} />
        ) : (
          ACTION_ICONS[action.type] || <UploadIcon sx={{ fontSize: 18 }} />
        )}
      </Box>

      {/* Content */}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography
          variant="body2"
          fontWeight={500}
          noWrap
          sx={{ fontSize: '0.8rem', color: '#1e293b' }}
        >
          {action.title}
        </Typography>
        <Typography
          variant="caption"
          noWrap
          sx={{ color: '#64748b', display: 'block' }}
        >
          {action.subtitle || STATUS_LABELS[action.status]}
        </Typography>

        {/* Progress bar for active actions */}
        {isActive && (
          <LinearProgress
            variant={action.progress > 0 ? 'determinate' : 'indeterminate'}
            value={action.progress}
            sx={{
              mt: 0.75,
              height: 3,
              borderRadius: 2,
              bgcolor: '#f1f5f9',
              '& .MuiLinearProgress-bar': {
                bgcolor: '#0ea5e9',
                borderRadius: 2,
              },
            }}
          />
        )}

        {isFailed && action.errorMessage && (
          <Typography variant="caption" sx={{ color: '#dc2626', display: 'block', mt: 0.25 }}>
            {action.errorMessage}
          </Typography>
        )}
      </Box>

      {/* Right side: time + dismiss */}
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 0.5, flexShrink: 0 }}>
        <Typography variant="caption" sx={{ color: '#94a3b8', fontSize: '0.65rem' }}>
          {timeAgo(action.createdAt)}
        </Typography>
        {!isActive && (
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              onDismiss(action._id);
            }}
            sx={{ p: 0.25 }}
          >
            <CloseIcon sx={{ fontSize: 14, color: '#94a3b8' }} />
          </IconButton>
        )}
      </Box>
    </Box>
  );
};

/** Toast notification for completed/failed actions */
const ActionToast: React.FC<{
  action: UserAction;
  onClose: () => void;
  onNavigate: (path: string) => void;
}> = ({ action, onClose, onNavigate }) => {
  const isDone = action.status === UserActionStatus.COMPLETED;

  return (
    <Snackbar
      open
      autoHideDuration={8000}
      onClose={onClose}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      TransitionComponent={Slide}
      sx={{ mb: 1 }}
    >
      <Paper
        elevation={6}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          px: 2,
          py: 1.5,
          borderRadius: 2,
          borderLeft: `4px solid ${isDone ? '#059669' : '#dc2626'}`,
          minWidth: 320,
          maxWidth: 420,
        }}
      >
        {isDone ? (
          <DoneIcon sx={{ color: '#059669', fontSize: 22 }} />
        ) : (
          <ErrorIcon sx={{ color: '#dc2626', fontSize: 22 }} />
        )}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="body2" fontWeight={600} noWrap sx={{ fontSize: '0.8rem' }}>
            {action.title}
          </Typography>
          <Typography variant="caption" sx={{ color: '#64748b' }}>
            {isDone
              ? action.result?.message || 'Completed successfully'
              : action.errorMessage || 'An error occurred'}
          </Typography>
        </Box>
        {isDone && action.result?.navigateTo && (
          <Button
            size="small"
            variant="text"
            onClick={() => {
              onNavigate(action.result!.navigateTo!);
              onClose();
            }}
            sx={{ fontWeight: 600, fontSize: '0.75rem', whiteSpace: 'nowrap' }}
          >
            View
          </Button>
        )}
        <IconButton size="small" onClick={onClose} sx={{ p: 0.25 }}>
          <CloseIcon sx={{ fontSize: 16 }} />
        </IconButton>
      </Paper>
    </Snackbar>
  );
};

interface ProcessMonitorWidgetProps {
  /** Styles for the header icon button, so it matches the host toolbar. */
  buttonSx?: SxProps<Theme>;
}

/** Action Center: header icon with a live badge, opening a dropdown of recent actions */
const ProcessMonitorWidget: React.FC<ProcessMonitorWidgetProps> = ({ buttonSx }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const {
    actions,
    activeCount,
    notifications,
    dismissAction,
    dismissAll,
    dismissNotification,
  } = useProcessMonitor();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const open = Boolean(anchorEl);

  const handleNavigate = (path: string) => {
    navigate(path);
    setAnchorEl(null);
  };

  const visibleActions = actions.slice(0, 15);

  return (
    <>
      <Tooltip title={activeCount > 0 ? `Action Center (${activeCount} running)` : 'Action Center'}>
        <IconButton onClick={(e) => setAnchorEl(e.currentTarget)} sx={buttonSx}>
          <Badge
            badgeContent={activeCount || undefined}
            sx={{
              '& .MuiBadge-badge': {
                bgcolor: '#0ea5e9',
                color: '#fff',
                fontWeight: 600,
              },
            }}
          >
            <ActionCenterIcon
              sx={{
                color: activeCount > 0 ? '#0ea5e9' : undefined,
                animation: activeCount > 0 ? 'actionCenterPulse 2s ease-in-out infinite' : 'none',
                '@keyframes actionCenterPulse': {
                  '0%, 100%': { opacity: 1 },
                  '50%': { opacity: 0.45 },
                },
              }}
            />
          </Badge>
        </IconButton>
      </Tooltip>

      {/* Toast notifications, top-right under the header so the AI chat corner stays clear */}
      <Box
        sx={{
          position: 'fixed',
          top: 72,
          right: 16,
          zIndex: theme.zIndex.snackbar + 1,
          display: 'flex',
          flexDirection: 'column',
          gap: 1,
        }}
      >
        {notifications.map((n) => (
          <ActionToast
            key={n._id}
            action={n}
            onClose={() => dismissNotification(n._id)}
            onNavigate={handleNavigate}
          />
        ))}
      </Box>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{
          paper: {
            sx: {
              mt: 1,
              width: 380,
              maxHeight: 460,
              borderRadius: 2,
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column',
              border: `1px solid ${alpha('#0ea5e9', 0.15)}`,
            },
          },
        }}
      >
        {/* Header */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            px: 2,
            py: 1.5,
            borderBottom: '1px solid',
            borderColor: 'divider',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="subtitle2" fontWeight={600} sx={{ fontSize: '0.9rem' }}>
              Action Center
            </Typography>
            {activeCount > 0 && (
              <Chip
                label={`${activeCount} active`}
                size="small"
                sx={{
                  height: 20,
                  fontSize: '0.65rem',
                  bgcolor: alpha('#0ea5e9', 0.12),
                  color: '#0284c7',
                  fontWeight: 600,
                }}
              />
            )}
          </Box>
          {actions.length > 0 && (
            <Button
              size="small"
              startIcon={<ClearAllIcon sx={{ fontSize: 16 }} />}
              onClick={dismissAll}
              sx={{ fontSize: '0.75rem', color: '#64748b', textTransform: 'none' }}
            >
              Clear completed
            </Button>
          )}
        </Box>

        {/* Action list */}
        <Box sx={{ flex: 1, overflow: 'auto', bgcolor: '#fff' }}>
          {visibleActions.length === 0 ? (
            <Box sx={{ p: 3, textAlign: 'center' }}>
              <Typography variant="body2" color="text.secondary">
                No recent actions
              </Typography>
            </Box>
          ) : (
            visibleActions.map((action) => (
              <ActionRow
                key={action._id}
                action={action}
                onDismiss={dismissAction}
                onNavigate={handleNavigate}
              />
            ))
          )}
        </Box>
      </Popover>
    </>
  );
};

export default ProcessMonitorWidget;
